import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';

const CATEGORIES = ['Asia', 'Europe', 'Americas', 'Africa', 'Oceania', 'Custom'];

// Image resize utility for uploaded files
const resizeImage = (file, maxSizeKB = 50) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (max 200x150 for flags)
      const maxWidth = 200;
      const maxHeight = 150;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Start with high quality and reduce if needed
      let quality = 0.9;
      let dataURL = canvas.toDataURL('image/png', quality);
      
      // Calculate file size and reduce quality if needed
      const getBase64Size = (base64String) => {
        const stringLength = base64String.length - 'data:image/png;base64,'.length;
        return Math.round(stringLength * 0.75);
      };
      
      while (getBase64Size(dataURL) > maxSizeKB * 1024 && quality > 0.1) {
        quality -= 0.1;
        dataURL = canvas.toDataURL('image/jpeg', quality);
      }
      
      resolve(dataURL);
    };
    
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const CountryBattleTeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [flagInputType, setFlagInputType] = useState('url'); // 'url' or 'upload'
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    flag: '',
    category: 'Custom',
    isActive: true,
    order: 0
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/country-battle/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      setMessage('Error fetching teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code || !formData.flag) {
      setMessage('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingTeam 
        ? `${API_BASE_URL}/api/admin/country-battle/teams/${editingTeam._id}`
        : `${API_BASE_URL}/api/admin/country-battle/teams`;
      
      const response = await fetch(url, {
        method: editingTeam ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage(editingTeam ? 'Team updated successfully!' : 'Team added successfully!');
        fetchTeams();
        resetForm();
      } else {
        setMessage(data.message || 'Error saving team');
      }
    } catch (error) {
      setMessage('Error saving team');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/country-battle/teams/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setMessage('Team deleted successfully!');
        fetchTeams();
      } else {
        setMessage('Error deleting team');
      }
    } catch (error) {
      setMessage('Error deleting team');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      code: team.code,
      flag: team.flag,
      category: team.category,
      isActive: team.isActive,
      order: team.order || 0
    });
    setShowAddForm(true);
  };

  const handleImportDefaults = async () => {
    if (!window.confirm('This will import default countries. Existing teams will be skipped. Continue?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/country-battle/teams/import-defaults`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      setMessage(data.message);
      fetchTeams();
    } catch (error) {
      setMessage('Error importing default countries');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      flag: '',
      category: 'Custom',
      isActive: true,
      order: 0
    });
    setEditingTeam(null);
    setShowAddForm(false);
    setFlagInputType('url');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const base64Image = await resizeImage(file, 100); // Max 100KB after compression
      setFormData({ ...formData, flag: base64Image });
      setMessage('Image uploaded successfully!');
    } catch (error) {
      setMessage('Error processing image');
    } finally {
      setUploading(false);
    }
  };

  const filteredTeams = filterCategory === 'all'
    ? teams 
    : teams.filter(t => t.category === filterCategory);

  const styles = {
    container: {
      padding: '24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1a1a2e',
      margin: 0
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    primaryBtn: {
      backgroundColor: '#4361ee',
      color: 'white'
    },
    secondaryBtn: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    dangerBtn: {
      backgroundColor: '#dc3545',
      color: 'white'
    },
    successBtn: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    message: {
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb'
    },
    filterSection: {
      display: 'flex',
      gap: '12px',
      marginBottom: '20px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    filterSelect: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: '1px solid #dee2e6',
      fontSize: '14px',
      cursor: 'pointer'
    },
    form: {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '20px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#495057'
    },
    input: {
      padding: '10px 12px',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    select: {
      padding: '10px 12px',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
      backgroundColor: 'white'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    previewSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    previewImage: {
      width: '80px',
      height: '50px',
      objectFit: 'contain',
      borderRadius: '4px',
      border: '1px solid #dee2e6'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '16px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transition: 'transform 0.2s',
      border: '1px solid #e9ecef'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    flagImage: {
      width: '60px',
      height: '40px',
      objectFit: 'contain',
      borderRadius: '4px',
      border: '1px solid #dee2e6'
    },
    teamInfo: {
      flex: 1
    },
    teamName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a2e',
      margin: 0
    },
    teamCode: {
      fontSize: '12px',
      color: '#6c757d',
      margin: '4px 0 0 0'
    },
    categoryBadge: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: '#e9ecef',
      color: '#495057'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500'
    },
    activeBadge: {
      backgroundColor: '#d4edda',
      color: '#155724'
    },
    inactiveBadge: {
      backgroundColor: '#f8d7da',
      color: '#721c24'
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 'auto',
      paddingTop: '12px',
      borderTop: '1px solid #e9ecef'
    },
    cardActions: {
      display: 'flex',
      gap: '8px'
    },
    smallBtn: {
      padding: '6px 12px',
      fontSize: '12px',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer'
    },
    stats: {
      display: 'flex',
      gap: '20px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    statCard: {
      backgroundColor: 'white',
      padding: '16px 24px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      minWidth: '120px'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#4361ee'
    },
    statLabel: {
      fontSize: '14px',
      color: '#6c757d'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#6c757d'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      color: '#6c757d'
    },
    helpText: {
      fontSize: '12px',
      color: '#6c757d',
      marginTop: '4px'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading teams...</div>;
  }

  const activeCount = teams.filter(t => t.isActive).length;
  const categoryCount = {};
  teams.forEach(t => {
    categoryCount[t.category] = (categoryCount[t.category] || 0) + 1;
  });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🏆 Country Battle Teams</h1>
        <div style={styles.buttonGroup}>
          <button 
            style={{...styles.button, ...styles.successBtn}}
            onClick={handleImportDefaults}
          >
            📥 Import Defaults
          </button>
          <button 
            style={{...styles.button, ...styles.primaryBtn}}
            onClick={() => { resetForm(); setShowAddForm(true); }}
          >
            ➕ Add Team
          </button>
        </div>
      </div>

      {message && (
        <div style={styles.message} onClick={() => setMessage('')}>
          {message}
        </div>
      )}

      <div style={styles.stats}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{teams.length}</div>
          <div style={styles.statLabel}>Total Teams</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{activeCount}</div>
          <div style={styles.statLabel}>Active</div>
        </div>
        {Object.entries(categoryCount).slice(0, 4).map(([cat, count]) => (
          <div key={cat} style={styles.statCard}>
            <div style={styles.statValue}>{count}</div>
            <div style={styles.statLabel}>{cat}</div>
          </div>
        ))}
      </div>

      {showAddForm && (
        <div style={styles.form}>
          <h3 style={{marginTop: 0, marginBottom: '20px'}}>
            {editingTeam ? '✏️ Edit Team' : '➕ Add New Team'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Team Name *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., INDIA"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value.toUpperCase()})}
                  required
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Country Code *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., IN"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  maxLength={10}
                  required
                />
                <span style={styles.helpText}>2-3 letter ISO code (e.g., IN, US, GB-ENG)</span>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Flag Image *</label>
                <div style={{display: 'flex', gap: '12px', marginBottom: '10px'}}>
                  <button
                    type="button"
                    style={{
                      ...styles.button,
                      ...(flagInputType === 'url' ? styles.primaryBtn : styles.secondaryBtn),
                      padding: '8px 16px'
                    }}
                    onClick={() => setFlagInputType('url')}
                  >
                    🔗 URL Link
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.button,
                      ...(flagInputType === 'upload' ? styles.primaryBtn : styles.secondaryBtn),
                      padding: '8px 16px'
                    }}
                    onClick={() => setFlagInputType('upload')}
                  >
                    📤 Upload Image
                  </button>
                </div>
                
                {flagInputType === 'url' ? (
                  <>
                    <input
                      type="url"
                      style={styles.input}
                      placeholder="https://flagcdn.com/w160/in.png"
                      value={formData.flag.startsWith('data:') ? '' : formData.flag}
                      onChange={(e) => setFormData({...formData, flag: e.target.value})}
                    />
                    <span style={styles.helpText}>Use flagcdn.com/w160/[code].png format or any image URL</span>
                  </>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      style={{...styles.input, padding: '8px'}}
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    <span style={styles.helpText}>
                      {uploading ? '⏳ Processing image...' : 'Upload PNG, JPG, or GIF (max 5MB)'}
                    </span>
                  </>
                )}
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <select
                  style={styles.select}
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Display Order</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                  <span>Active</span>
                </div>
              </div>
            </div>
            
            {formData.flag && (
              <div style={styles.previewSection}>
                <span style={{fontWeight: '500'}}>Preview:</span>
                <img 
                  src={formData.flag} 
                  alt="Flag preview" 
                  style={styles.previewImage}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span>{formData.name || 'Team Name'} ({formData.code || 'CODE'})</span>
              </div>
            )}
            
            <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
              <button type="submit" style={{...styles.button, ...styles.primaryBtn}}>
                {editingTeam ? '💾 Update Team' : '➕ Add Team'}
              </button>
              <button 
                type="button" 
                style={{...styles.button, ...styles.secondaryBtn}}
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.filterSection}>
        <span style={{fontWeight: '500'}}>Filter by:</span>
        <select
          style={styles.filterSelect}
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <span style={{color: '#6c757d', marginLeft: '12px'}}>
          Showing {filteredTeams.length} teams
        </span>
      </div>

      {filteredTeams.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={{fontSize: '18px', marginBottom: '8px'}}>No teams found</p>
          <p>Click "Import Defaults" to add common countries or "Add Team" to create custom teams.</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredTeams.map(team => (
            <div key={team._id} style={styles.card}>
              <div style={styles.cardHeader}>
                <img 
                  src={team.flag} 
                  alt={team.name} 
                  style={styles.flagImage}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/60x40?text=?'; }}
                />
                <div style={styles.teamInfo}>
                  <h3 style={styles.teamName}>{team.name}</h3>
                  <p style={styles.teamCode}>{team.code}</p>
                </div>
              </div>
              
              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                <span style={styles.categoryBadge}>{team.category}</span>
                <span style={{
                  ...styles.statusBadge,
                  ...(team.isActive ? styles.activeBadge : styles.inactiveBadge)
                }}>
                  {team.isActive ? '✓ Active' : '✗ Inactive'}
                </span>
              </div>
              
              <div style={styles.cardFooter}>
                <span style={{fontSize: '12px', color: '#6c757d'}}>
                  Order: {team.order}
                </span>
                <div style={styles.cardActions}>
                  <button 
                    style={{...styles.smallBtn, ...styles.primaryBtn}}
                    onClick={() => handleEdit(team)}
                  >
                    Edit
                  </button>
                  <button 
                    style={{...styles.smallBtn, ...styles.dangerBtn}}
                    onClick={() => handleDelete(team._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountryBattleTeamManagement;
